import { localAddressFor } from './answer-ref-alias.js';
import { answerRefJointReach, answerRefReach } from './answer-ref.js';
import { aliasDrivenRowId } from './alias-driver.js';
import { drivenRowIdFor } from './driven-rows.js';
const ALIAS_PREFIX = 'alias:';
export function parseDriverAddress(value) {
    if (typeof value !== 'string')
        return null;
    if (value.startsWith(ALIAS_PREFIX)) {
        const aliasId = value.slice(ALIAS_PREFIX.length);
        return aliasId ? { kind: 'alias', aliasId } : null;
    }
    const [taskId, blockId, facet] = value.split(':');
    // Both ids or nothing: half an address resolves to nothing, or worse is completed later by
    // a block that answers a different question — `planReconnections`' rule, one layer down.
    if (!taskId || !blockId)
        return null;
    return facet ? { kind: 'block', taskId, blockId, facet } : { kind: 'block', taskId, blockId };
}
export const formatDriverAddress = (a) => (a.kind === 'alias' ? `${ALIAS_PREFIX}${a.aliasId}`
    : a.facet ? `${a.taskId}:${a.blockId}:${a.facet}` : `${a.taskId}:${a.blockId}`);
/**
 * THE PROJECTION: the block's versions are the driver's rows, in the driver's order.
 *
 * Deliberately NOT `syncScenarioBranches`, which the scope expected to reuse. That one DROPS a
 * row whose scenario is gone and hands it back in `removed`, which is right for an answer setup
 * and wrong for a question: a deleted driver option would silently take an authored stem, its
 * feedback and its branches with it. So a stranded version is kept, appended after the live
 * ones, and returned in `orphaned` for the caller to report. Nothing destructive happens
 * without the teacher asking, one level down from the driver-loss rule itself.
 *
 *   - a version whose row still exists is kept EXACTLY as it is, including `unreviewed`
 *   - a row with no version gains one from `template`, marked `unreviewed`
 *   - a version whose row is gone is KEPT UNCHANGED, and reported — see the note below on why
 *     it does not lose its `unreviewed` mark on the way out
 *
 * `rows` of `null` means the block is not driven, and the versions are returned untouched —
 * which is what makes this safe to call unconditionally.
 */
export function syncDrivenVersions(rows, existing, template) {
    const have = existing ?? [];
    if (!rows)
        return { versions: have, orphaned: [] };
    const byRow = new Map();
    for (const v of have)
        if (v.drivenRowId !== undefined)
            byRow.set(v.drivenRowId, v);
    /*
     * WHICH STORED VERSIONS WERE USED, by identity — and tracked here rather than inferred from
     * `live` below, which is what `placed` used to do. A row whose words moved comes back as a
     * NEW object, so an identity check against the output declared the original an orphan and
     * appended it: one rename produced two versions for one row, the stale copy sitting after
     * Blank where the pager would still page through it.
     */
    const used = new Set();
    /*
     * PASS 1: the row this version was already on. Exact, and the only pass a block driver needs.
     */
    const paired = rows.map(row => {
        const kept = byRow.get(row.id);
        if (kept)
            used.add(kept);
        return { row, kept, renamed: false };
    });
    /*
     * PASS 2: THE SAME OPTION UNDER NEW WORDS — the alias axis only, by construction.
     *
     * An alias union row is keyed by the option's words, so renaming an option is
     * indistinguishable from deleting it and adding another: the version strands and a blank row
     * takes its place. A block driver's rows carry no `sourceOptions`, so nothing here fires for
     * them — this is alias-shaped without being alias-special-cased.
     *
     * ONLY ROWS THAT FOUND NOTHING, against ONLY VERSIONS NOTHING CLAIMED, so the two cases that
     * are not renames fall out correctly on their own:
     *   - a MERGE ("bear" renamed to "ocean" where a member already says "ocean") finds the
     *     surviving class already claimed in pass 1, and the second version strands — right,
     *     since one row cannot hold two answer keys
     *   - a SPLIT (one of two members reworded) leaves the surviving class claimed and the new
     *     one empty, so the new one is templated — right, it is a genuinely new equivalence
     */
    const spare = have.filter(v => !used.has(v) && (v.drivenRowSources?.length ?? 0) > 0);
    if (spare.length > 0) {
        for (const slot of paired) {
            const from = slot.row.sourceOptions;
            if (slot.kept || !from?.length)
                continue;
            const hit = spare.find(v => !used.has(v) && v.drivenRowSources.some(o => from.includes(o)));
            if (!hit)
                continue;
            used.add(hit);
            slot.kept = hit;
            slot.renamed = true;
        }
    }
    const live = paired.map(({ row, kept, renamed }) => {
        if (!kept) {
            return { ...template(row), drivenRowId: row.id, unreviewed: true,
                drivenRowLabel: row.label, drivenRowSources: row.sourceOptions };
        }
        /*
         * CARRIED ACROSS A RENAME: the authored question and answer key keep their work and move to
         * the row those options are in now, back to undecided — which is exactly what the same
         * rename does on a block driver, and the whole point of recognising it.
         *
         * `drivenRowSources` is NOT restated here, and that is deliberate rather than an omission:
         * the record is a list of option IDS, which a rename does not touch, so the spread already
         * carries the right one. Restating it survived every mutation I could build against it,
         * which is the signal that it was a line no behaviour depended on.
         */
        if (renamed) {
            return { ...kept, drivenRowId: row.id, unreviewed: true, drivenRowLabel: row.label };
        }
        /* The contributors can change without the words moving — a member joining or leaving an
           equivalence. That is not something to re-decide, so the record is refreshed in silence. */
        const sourcesMoved = row.sourceOptions !== undefined
            && (kept.drivenRowSources ?? []).join('\u0000') !== row.sourceOptions.join('\u0000');
        /*
         * WHILE IT IS UNDECIDED THE LABEL TRACKS THE ROW, so the moment a teacher reviews it the
         * recorded words are the ones they were looking at. Nothing else maintains it: the write
         * that reviews a version goes through `markVersionReviewed`, which has the version and
         * not the row.
         */
        if (kept.unreviewed) {
            return kept.drivenRowLabel === row.label && !sourcesMoved
                ? kept
                : { ...kept, drivenRowLabel: row.label, drivenRowSources: row.sourceOptions };
        }
        /*
         * DECIDED, AND THE WORDS HAVE MOVED SINCE — see `drivenRowLabel`. Back to undecided, with
         * the new words recorded, so it asks to be looked at exactly once per rename.
         *
         * A version with no recorded label predates this and makes no claim about what it was
         * decided against, so it is left alone: every old bank announcing itself unreviewed at
         * once would be worse than the silence it replaces.
         */
        if (kept.drivenRowLabel === undefined || kept.drivenRowLabel === row.label) {
            return sourcesMoved ? { ...kept, drivenRowSources: row.sourceOptions } : kept;
        }
        return { ...kept, unreviewed: true, drivenRowLabel: row.label,
            drivenRowSources: row.sourceOptions };
    });
    /*
     * AN ORPHAN IS PARKED DATA, NOT PENDING WORK — and that is the READER'S business, not a
     * reason to edit the version on the way out.
     *
     * It used to be done here: a stranded version had `unreviewed` cleared, after a report that a
     * variant deleted while a follower's row was still unreviewed left the block permanently
     * reading "1 unreviewed" with nothing to review. The count was the thing that was wrong, and
     * clearing the flag fixed the count by destroying the fact behind it.
     *
     * What the fact is for: `unreviewed` means "a projected copy nobody has touched", and it is
     * the only record of that. `useDrivenVersionProjection` reads it to decide whether a bank is
     * safe to DISCARD when the teacher re-points the driver, and clearing it on park poisoned
     * that gate. The sequence is DELETE AN OPTION, then switch: the deleted option's version is
     * parked here, loses its mark, and the bank now reads as authored — so the switch keeps it,
     * and so does every switch after that. Measured in the browser: a block re-pointed four
     * times held nine versions, five of them answering options that no longer exist. Reported as
     * "this is blowing up my answer pile ... it's at 13 now".
     *
     * So the flag is durable now and the two readers that count it — `use-driven-link-issues`
     * and `varies-with-section` — ask their own question, which was always "how many versions a
     * student can REACH are undecided". Both already hold the rows needed to say so.
     */
    const orphaned = have.filter(v => !used.has(v));
    return { versions: [...live, ...orphaned], orphaned };
}
/**
 * WHICH VERSION THE STUDENT'S OWN ANSWER SELECTS.
 *
 * Returns a version id for `pickVersion`, or null when nothing applies. Null rather than the
 * first version, for the reason `pickScenarioBranch` gives: `pickVersion` falls back to version
 * one because a block must show SOMETHING, and falling back HERE would ask a student the Mars
 * question about Titan. A driven axis has a Blank row precisely so the fallback is never
 * needed — reaching null means the projection has not run or the row's version was stranded,
 * both of which are authoring errors the issue list already names.
 */
export function drivenVersionIdFor(versions, 
/** The driver's block data, as the student's edition pinned it. */
driver, 
/** That block's submitted answer, in whatever shape the block records. */
answer, facet) {
    if (!versions?.length || !driver)
        return null;
    const rowId = drivenRowIdFor(driver.type, driver.data, answer, facet);
    if (rowId === null)
        return null;
    return versions.find(v => v.drivenRowId === rowId)?.id ?? null;
}
/**
 * Let go of the driver, keeping every version.
 *
 * State 1 of the driver-loss rule: a paste that cannot resolve the address clears the link and
 * leaves the content. The `drivenRowId`s are kept ON PURPOSE — they are what a reconnect to the
 * same driver matches on, so the round trip is lossless, and they are inert to everything else.
 */
export function releaseDriver(data) {
    return data.drivenBy === undefined ? data : { ...data, drivenBy: undefined };
}
/**
 * LET GO OF THE AXIS AND COLLAPSE TO ONE QUESTION — state 3, and ONLY on the teacher's say-so.
 *
 * Dropping the link to None is the one moment a teacher has said the driver is not what they
 * want, and the alternative questions only ever existed to answer it. So they go, the way the
 * other two "stop varying" controls in this editor make theirs go:
 *
 *   - unlinking a SCENARIO writes `scenarioBranches: undefined` (`linkToLeader`), dropping
 *     every per-scenario answer setup, with an in-memory bank for a relink in the same sitting;
 *   - unticking BRANCHING CORRECTS clears `conditionalBranches`, keeping a draft that is only
 *     offered back while the block stays open — the owner's reasoning being that a branching
 *     correct you have long forgotten is confusing rather than helpful.
 *
 * Both drop, both bank briefly, and in both the real recovery is UNDO. This is one emit, so
 * undo restores the whole axis in a single step, and that is the bank: a second in-memory one
 * here would be a third mechanism for a round trip that is already covered.
 *
 * ## It collapses rather than clearing, which is the one asymmetry
 *
 * A scenario row and a branch row are ANSWER SETUPS hanging off a question that still exists
 * underneath them, so clearing them leaves a complete block. A driven bank is a set of whole
 * QUESTIONS and there is no block-level question beneath it — version one IS the block's
 * question. Clearing would leave a block with nothing to ask.
 *
 * So version one is folded back onto the block and the rest are discarded, which is
 * `removeVersionWith`'s own last-survivor rule and the scope's "first row wins", taken from
 * dropping a scenario connection. `applyVersion` is the block's own, passed the way
 * `useDrivenVersionProjection` takes `addVersion`: both are `(data, …) => data` on every block,
 * so the call is identical at all twelve sites.
 *
 * An EARLIER build of this kept every version as an ordinary drawn bank, on the grounds that
 * the delete button could reach the same place. It could, N-1 presses at a time, and it left
 * the one control that means "stop varying" as the only one that did not.
 *
 * Clearing `drivenBy` ALONE would be neither, and was the original trap: the versions keep
 * their row ids, `versionAxisOwner` reads `orphaned`, the block reports an error, and
 * `+ Version` and the scenario link are both refused — an escape hatch with no exit.
 */
export function collapseDrivenAxis(data, applyVersion) {
    const released = releaseDriver(data);
    const keep = data.versions?.[0];
    if (!keep)
        return released;
    /* `drivenBy` cleared AFTER the fold, not before: `applyVersion` rebuilds the block through
       its own `initData`, which carries `drivenBy` across — deliberately, since that is what
       stopped the field being dropped on every write. Releasing first alone would put it back. */
    return { ...applyVersion(released, keep.id), versions: undefined, drivenBy: undefined };
}
export function versionAxisOwner(data) {
    if (data?.drivenBy)
        return 'driven';
    if (data?.followsBlockId)
        return 'scenario';
    const versions = data?.versions ?? [];
    if (versions.some(v => v.drivenRowId !== undefined))
        return 'orphaned';
    return versions.length ? 'drawn' : 'none';
}
/** Versions projected from a driver, which the teacher may page and may not add to or delete.
 *  Reads the versions themselves rather than the link, so it still holds once the link is lost. */
export const isDrivenAxis = (versions) => (versions ?? []).some(v => v.drivenRowId !== undefined);
/**
 * What stands in the way of a choice, or null when nothing does.
 *
 * Returns the OWNER rather than a message, so the reason is mechanically derived from the one
 * state — the surface turns it into words. Four reasons hand-written per surface is four
 * chances to explain a refusal by the wrong cause.
 *
 * `orphaned` blocks everything but reconnecting, which is the forcing function the owner asked
 * for: the block has authored versions and nothing to select among them, and the teacher must
 * either point it at a driver again or say None and collapse them. Nothing destructive happens
 * on its own, and nothing sits silently orphaned either.
 *
 * ## A DRAWN BANK MAY STILL FOLLOW A SCENARIO, and may not be driven
 *
 * The asymmetry is real and is not a rule about what is sound — it is about what is built.
 * Linking a banked block to a scenario has its own three-way offer (`ScenarioRemapModal`,
 * §7.1): pair each version with a row, discard the bank, or cancel. A migration, not a
 * refusal, and refusing it here would have broken a designed flow — which is how this was
 * found, by eighteen of its tests failing at once.
 *
 * Nothing equivalent exists for a driver, and the default is worse than refusing: every
 * authored version would land in `syncDrivenVersions`'s `orphaned` list, since none carries a
 * row id. So it is blocked with a reason, and the same three-way offer is what would unblock
 * it. Costed as a limit rather than worked around.
 */
export function axisBlockedBy(owner, choice) {
    if (owner === 'none')
        return null;
    if (choice === 'driven')
        return owner === 'driven' || owner === 'orphaned' ? null : owner;
    if (choice === 'scenario')
        return owner === 'scenario' || owner === 'drawn' ? null : owner;
    return owner === 'drawn' ? null : owner;
}
/**
 * EVERY DRIVEN BLOCK'S VERSION, keyed exactly as the roll keys a drawn one.
 *
 * The runtime half, and it rides the existing key rather than a second map. Twelve blocks read
 * `taskPreviewRoll[versionRollKey(block.id)]` to find the version a student is being asked, and
 * a parallel channel would be twelve sites that have to learn about it — which is how MCQ and
 * Dropdown came to lag a commit on the link control.
 *
 * DERIVED, NOT DRAWN, and the two stay apart where it matters: `rollVersions` refuses to emit a
 * driven key, so the roll the session STORES is only ever what was drawn. This merges on top of
 * it for the surfaces that read it. A block is drawn or it is driven, so the two can never write
 * the same key.
 *
 * `answerAt` keeps the session out of this package — it is the caller's `lastAnswersFor`.
 *
 * ## AN UNANSWERED DRIVER IS BLANK, WHATEVER THE REASON
 *
 * `answerAt` returns `undefined` for "the student skipped it", for "they branched past it" and
 * for "nobody has been there", and the Blank row is the honest answer to all three: a student
 * who has not answered the driver HAS NOT ANSWERED IT, and that is the one version written for
 * them. Owner, settling it: "a student who enters a driven-by question that references an
 * answer on another pathway did not answer that question, and so the correct interpretation
 * would be No Answer".
 *
 * This had an `attempted` veto that left NO entry for an unattempted driver's task, so
 * `pickVersion` fell back to version one. It was added for "it's perpetually stuck on the last
 * (no answer) option in preview", and it cost more than it bought: a student who took the other
 * branch was asked the FIRST version — a question about an answer they never gave — and the
 * reach ladder and the issue list both told the teacher nothing, because both say in writing
 * that Blank catches those routes. It does now.
 *
 * What the veto was really guarding is a teacher inspecting mid-experience, and that has its
 * own control: the version cycler beside Edit sets a stand-in for the driver, which reaches
 * here through `answerAt` and moves the version in the editor AND in Preview. The canvas cards
 * never came through this path at all — outside Preview the roll is null and they resolve
 * through `useDrivenVersionInForce`, which samples the driver's CORRECT answer.
 */
export function drivenVersionRoll(blocks, tasks, answerAt, rollKey, opts) {
    const out = {};
    for (const block of blocks ?? []) {
        const address = parseDriverAddress(block.data?.drivenBy);
        if (!address)
            continue;
        const versions = block.data?.versions;
        /*
         * AN ALIAS RESOLVES THROUGH ITS OWN MODULE, which owns the union and the translation
         * from a member's option into a union row. Done here it would be a second merge rule,
         * and a merge that disagreed with the authored rows is a student shown the wrong
         * question — the exact failure the whole axis is built to prevent.
         */
        if (address.kind === 'alias') {
            const alias = opts?.aliases?.find(a => a.id === address.aliasId);
            if (!alias)
                continue;
            const rowId = aliasDrivenRowId(alias, tasks, answerAt, opts?.experienceVariableNames ?? new Set());
            const found = rowId === null
                ? null : versions?.find(v => v.drivenRowId === rowId)?.id ?? null;
            if (found !== null)
                out[rollKey(block.id)] = found;
            continue;
        }
        const driver = tasks.find(tk => tk.id === address.taskId)
            ?.blocks?.find(b => b.id === address.blockId);
        if (!driver)
            continue;
        const id = drivenVersionIdFor(versions, { type: driver.type, data: (driver.data ?? {}) }, answerAt(address.taskId, address.blockId), address.facet);
        /*
         * A NULL LEAVES NO ENTRY, which is the right failure. `pickVersion` then falls back to the
         * first version — the hazard this whole design avoids — but only in the states the editor
         * already reports as errors: the driver was deleted, or the row has no version. Writing a
         * guessed key would make those states invisible instead of merely wrong.
         */
        if (id !== null)
            out[rollKey(block.id)] = id;
    }
    return out;
}
/**
 * POINT A PASTED BLOCK AT THE DRIVER IT CAN STILL REACH, AND KEEP THE ADDRESS EITHER WAY.
 *
 * `relinkScenario` is the obvious sibling and is the WRONG one, which the scope got backwards
 * and `remapAnswerRefs` in the backend had already settled for the identical case:
 *
 *   "A dangling link is a shape the model forbids; a dangling REFERENCE is an ordinary broken
 *    chip the teacher can see and repair — and the original address is the only clue the
 *    repair has, including for matching against heritage. Clearing it would destroy the one
 *    thing that makes it fixable."
 *
 * A scenario link names a block in the SAME task, so a cross-task one cannot exist and a paste
 * that cannot resolve it must drop it. A driver is an ADDRESS, like an alias member, and a
 * broken address is an ordinary repairable state. So an unresolvable one is KEPT, and the
 * block's issue list says the linked block is missing — which names what to look for, where
 * clearing it would leave a teacher with versions and no idea what chose between them.
 *
 * The scope said "let go, keep versions", and the half that mattered to the owner is the half
 * kept: the VERSIONS are never lost and the block never sits quiet about it. Dropping the
 * address as well achieves neither of those and costs the repair.
 *
 * Resolution is `localAddressFor`, the alias members' own resolver, which already falls
 * through to `heritageId`. That fall-through is the whole of the duplicate case: a task
 * duplicated with a driven block inside it finds the ORIGINAL driver rather than a copy,
 * because the copy's heritage points back at the same block. Riding that table rather than
 * keeping a second one is the scope's rule, and the reason is on the record — two mechanisms
 * drifting is what caused five silent defects the last time references were copied.
 */
export function rebindDriver(
/** Every task the destination can see. */
tasks, data) {
    const drivenBy = data.drivenBy;
    const address = parseDriverAddress(drivenBy);
    // A malformed address resolves to nothing, so it is left exactly as it is.
    if (!drivenBy || !address)
        return data;
    /*
     * AN ALIAS ID IS DOCUMENT-SCOPED, so there is nothing to remap: the paste lands in the same
     * experience and the id still names the same alias, or it lands in another and the id names
     * nothing — which is an ordinary broken reference the teacher repairs, exactly as a broken
     * block address is. Rewriting it would need a rule for which alias in the destination means
     * the same thing, and no such rule exists. See `project_alias_identity_and_collisions`.
     */
    if (address.kind === 'alias')
        return data;
    const local = localAddressFor(tasks, { ...address, facet: '' });
    if (!local)
        return data;
    /* The FACET is the block's own, so it survives a remap untouched — a blank id is scoped to
       the block that holds it, and a copy of that block keeps its blanks' ids. */
    const next = formatDriverAddress({ kind: 'block', ...local, facet: address.facet });
    return next === drivenBy ? data : { ...data, drivenBy: next };
}
/**
 * HOW OFTEN A DRIVEN AXIS GETS AN ANSWER TO SELECT WITH — the `?` / `×` ladder, for the LINK.
 *
 * The same three states a recall chip wears, and they mean something different here, which is
 * why they are worth showing on the link rather than left to the chip rules:
 *
 *   - `always`    — every route reaches the driver, so a real answer always selects.
 *   - `sometimes` — a driver on a pathway the student may never take. INFORMATIONAL, not a
 *                   fault: the Blank row catches exactly those routes, which is what it is for.
 *                   A warning here would flag the commonest legitimate shape there is.
 *   - `never`     — the driver cannot be reached from here at all, so the axis can only ever
 *                   resolve to Blank and every other version is dead content. An authoring
 *                   ERROR, and the one state the teacher must be told about.
 *
 * A first version of this took a pre-computed `reach` and was never called by anything, which
 * is how it came to be deleted. This one asks the question itself, from the two things a caller
 * actually has — the stored address and the task graph — so there is nothing to compute first
 * and forget to pass.
 *
 * Reachability is `answerRefReach`, the recall chips' own, because it is the same question
 * about the same graph. An unresolvable address is `never`: nothing to reach.
 */
export function drivenLinkReach(drivenBy, referringTaskId, tasks, 
/**
 * Needed only for an alias link, and omitted by every block-link caller.
 *
 * An alias with no entry here reads `never`, which is the same answer a missing block gets
 * and the right one: an address that resolves to nothing is reached by nobody.
 */
aliases) {
    const address = parseDriverAddress(drivenBy);
    if (!address)
        return 'never';
    if (address.kind === 'alias') {
        /*
         * THE MEMBERS JOINTLY, which is the whole reason an alias link is worth having.
         *
         * Each member on its own is `sometimes` — that is what being on a pathway means — and
         * asking per member would report the commonest legitimate shape as a partial link. The
         * question is whether EVERY route reaches one of them, which is exactly
         * `answerRefJointReach`, the same function the recall chips' own coverage uses.
         */
        const alias = aliases?.find(a => a.id === address.aliasId);
        if (!alias)
            return 'never';
        return answerRefJointReach(alias.members.map(m => m.taskId), referringTaskId, tasks);
    }
    return answerRefReach(address.taskId, referringTaskId, tasks);
}
//# sourceMappingURL=driven-versions.js.map